/**
 * GH#1839 — la URL pública de un sitio de tenant SIN dominio propio.
 *
 * No existe DNS comodín `*.inbomarket.com` (#1433), así que `<subdominio>.inbomarket.com`
 * NO resuelve (NXDOMAIN). La URL que sí sirve es la ruta compartida
 * `app.inbomarket.com/p/<slug>`, y para que apunte al tenant correcto el slug tiene
 * que ser único (= el subdominio elegido, NO 'main' — con 'main' /p/main servía el
 * sitio más viejo de otro tenant).
 */
const HOST_PUBLICO = 'app.inbomarket.com';

/** Para mostrar (sin protocolo): app.inbomarket.com/p/<slug> */
export function urlPublicaSitio(slug: string): string {
  return `${HOST_PUBLICO}/p/${slug}`;
}

/**
 * GH#2866/#2869 — el slug `main` NO identifica a su dueño: 7 tenants tienen un sitio con ese slug
 * (DB, 15-oct-2026) y `/p/main` lo sirve UNO solo (el que está `active`, hoy Idecus). Los otros 6 verían
 * `app.inbomarket.com/p/main` como SU dirección y esa dirección abre el sitio de otro.
 *
 * Mientras no haya slugs únicos entre tenants (arreglo de fondo: GH#2869), lo que se MUESTRA como dirección
 * de un sitio con ese slug es «—». Coste aceptado por THEMIS y la CTO: Idecus tampoco ve esa dirección
 * para su sitio principal.
 */
export const SLUG_COMPARTIDO = 'main';

export function esSlugCompartido(slug: string): boolean {
  return slug.trim().toLowerCase() === SLUG_COMPARTIDO;
}

/** Lo que una pantalla muestra como dirección del sitio: la ruta pública, o «—» si el slug no identifica a su dueño. */
export function direccionMostrable(slug: string): string {
  return esSlugCompartido(slug) ? '—' : urlPublicaSitio(slug);
}

/**
 * GH#2869 — destino de un ENLACE a la dirección pública de un sitio, o null si el slug es compartido
 * (el enlace abriría el sitio de otro tenant). Quien reciba null pinta `direccionMostrable(slug)` como texto.
 */
export function hrefMostrable(slug: string): string | null {
  return esSlugCompartido(slug) ? null : hrefPublicoSitio(slug);
}

/**
 * GH#2869 — destino del botón «Ver en vivo» del panel, o null si no se debe pintar.
 *
 * `/p/<slug>` en el host compartido sirve el sitio `active` MÁS ANTIGUO con ese slug y no sirve
 * draft/disabled (soulbuilder/apps/web/src/lib/inbomarket-public.ts:158-172, leído en origin/main
 * 75b31d66016f). Para un slug compartido solo el sitio `active` abre SU sitio; en cualquier otro
 * estado el botón abriría el de otro tenant. Para un slug propio nada cambia.
 * Límite conocido: con DOS sitios `active` con el mismo slug, el más nuevo vería el botón y abriría
 * el del más antiguo; eso solo lo cierra el slug único (backend, GH#2869).
 */
export function hrefVerEnVivo(slug: string, status: string): string | null {
  if (esSlugCompartido(slug) && String(status ?? '').trim().toLowerCase() !== 'active') return null;
  return `/p/${slug}`;
}

/** Para enlazar (con protocolo): https://app.inbomarket.com/p/<slug> */
export function hrefPublicoSitio(slug: string): string {
  return `https://${HOST_PUBLICO}/p/${slug}`;
}
